import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Home as HomeIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import PropertyCard from "@/components/PropertyCard";
import AuthPromptDialog from "@/components/AuthPromptDialog";
import Seo from "@/components/Seo";
import NotFound from "@/pages/NotFound";
import AmenityIcon from "@/components/listing/AmenityIcon";
import CollectionLinks from "@/components/collections/CollectionLinks";
import ThingsToDoList from "@/components/collections/ThingsToDoList";
import { useAreas } from "@/hooks/useAreas";
import { useBrowseContext } from "@/hooks/useBrowseContext";
import { useCollections } from "@/hooks/useCollections";
import { useFavorites } from "@/hooks/useFavorites";
import { useProperties } from "@/hooks/useProperties";
import { copyVarsForTenant } from "@/hooks/useSiteCopy";
import { useTenant } from "@/hooks/useTenant";
import { filterProperties, promoteProperties } from "@/lib/browseFilters";
import { usePromotions } from "@/lib/monetization";
import {
  amenityStandardSpecs,
  collectionBrowseHref,
  collectionCopy,
  collectionPath,
  collectionSearch,
  describeSearch,
} from "@/lib/collections";
import { setIntent } from "@/lib/onboardingIntent";
import { breadcrumbList, collectionPageSchema, faqPage } from "@/lib/seo";
import StatBar from "@/components/StatBar";

/**
 * One collection: /collections/<slug> for the whole city, or
 * /collections/<slug>/<area> narrowed to a neighbourhood. Intro copy, the
 * live homes from the saved browse filter, the amenity standard, FAQ and
 * things to do, all from the admin's row, no page-specific code.
 */
const CollectionPage = () => {
  const { slug, area: areaSlug } = useParams();
  const tenant = useTenant();
  const { bySlug, collections, isLoading: collectionsLoading } = useCollections();
  const { bySlug: areasBySlug, areas, isLoading: areasLoading } = useAreas();
  const { data: properties = [], isLoading: propertiesLoading } = useProperties();
  const ctx = useBrowseContext(properties);
  const { toggleFavorite, isFavorite, authPromptOpen, setAuthPromptOpen } = useFavorites();

  const collection = slug ? bySlug.get(slug) ?? null : null;
  const areaKnown = !areaSlug || tenant.areaSlugs.includes(areaSlug);
  const area = areaSlug && areaKnown ? areasBySlug.get(areaSlug) ?? null : null;
  const vars = useMemo(() => copyVarsForTenant(tenant), [tenant]);
  const copy = useMemo(() => (collection ? collectionCopy(collection, vars, area) : null), [collection, vars, area]);
  const search = useMemo(() => (collection ? collectionSearch(collection, ctx, area?.slug) : null), [collection, ctx, area]);
  const promotions = usePromotions();
  // Paid featured homes first, as on Browse (Oct 20).
  const homes = useMemo(() => (search ? promoteProperties(filterProperties(properties, search, ctx), promotions, Math.floor(Date.now() / 86_400_000)) : []), [properties, search, ctx, promotions]);
  const chips = useMemo(() => (search ? describeSearch(search, ctx) : []), [search, ctx]);
  const standard = useMemo(() => (collection ? amenityStandardSpecs(collection) : []), [collection]);
  // Areas worth switching to: those with at least one matching home.
  const areaCounts = useMemo(
    () =>
      collection
        ? areas
            .map((a) => ({ area: a, count: filterProperties(properties, collectionSearch(collection, ctx, a.slug), ctx).length }))
            .filter((r) => r.count > 0)
        : [],
    [collection, areas, properties, ctx],
  );
  const thingsToDo = area ? area.thingsToDo : collection?.thingsToDo ?? [];

  if (collectionsLoading || (areaSlug && areasLoading)) {
    return <div className="min-h-[50vh] flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  }
  if (!collection || !copy || !search || (areaSlug && !area)) return <NotFound />;

  const path = collectionPath(collection.slug, area?.slug);
  const seoCtx = { siteUrl: tenant.siteUrl, siteName: tenant.siteName };
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Collections", path: "/collections" },
    { name: collectionCopy(collection, vars).title, path: collectionPath(collection.slug) },
    ...(area ? [{ name: area.name, path }] : []),
  ];
  const cityLabel = area ? area.name : tenant.cityLabel;

  return (
    <div className="pb-24">
      <Seo
        title={copy.seoTitle}
        description={copy.seoDescription}
        path={path}
        // A collection with nothing to show is a thin page: keep it out of the index until it fills.
        noindex={!propertiesLoading && homes.length === 0}
        jsonLd={[
          collectionPageSchema(seoCtx, {
            name: copy.title,
            description: copy.seoDescription,
            path,
            items: homes.slice(0, 25).map((p) => ({ name: p.title, url: `/properties/${p.slug}` })),
          }),
          breadcrumbList(seoCtx, crumbs),
          ...(collection.faq.length ? [faqPage(collection.faq.map((f) => ({ q: f.q, a: f.a })))] : []),
        ]}
      />

      {/* Hero */}
      <section className="bg-secondary/40 border-b border-border">
        <div className="container mx-auto px-4 pt-8 pb-10 md:pt-12 md:pb-14">
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-[13px] text-muted-foreground mb-4">
            {crumbs.slice(0, -1).map((c) => (
              <span key={c.path} className="inline-flex items-center gap-1.5">
                <Link to={c.path} className="hover:text-primary transition-colors">
                  {c.name}
                </Link>
                <span aria-hidden>›</span>
              </span>
            ))}
            <span className="text-foreground">{crumbs[crumbs.length - 1].name}</span>
          </nav>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight max-w-3xl">{copy.title}</h1>
          {chips.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {chips.map((c) => (
                <span key={c} className="inline-flex items-center gap-1 rounded-full bg-card border border-border px-3 py-1 text-[13px] text-foreground">
                  <Check className="h-3.5 w-3.5 text-primary" aria-hidden /> {c}
                </span>
              ))}
            </div>
          )}
          <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
            <p className="text-sm text-muted-foreground">
              {propertiesLoading ? "Counting homes…" : `${homes.length} ${homes.length === 1 ? "home" : "homes"} in ${cityLabel} right now`}
            </p>
            <Button asChild variant="outline" className="sm:ml-auto rounded-full">
              <Link to={collectionBrowseHref(collection, area?.slug)}>
                Open in browse <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <StatBar className="container mx-auto px-4 pt-8" />

      <div className="container mx-auto px-4">
        {/* Area switcher */}
        {areaCounts.length > 0 && (
          <section className="pt-6 flex flex-wrap items-center gap-2">
            <span className="text-[13px] text-muted-foreground mr-1">Where:</span>
            <Link
              to={collectionPath(collection.slug)}
              className={`rounded-full border px-3 py-1.5 text-[13px] transition-colors ${!area ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary/50"}`}
            >
              All of {tenant.cityLabel}
            </Link>
            {areaCounts.map(({ area: a, count }) => (
              <Link
                key={a.slug}
                to={collectionPath(collection.slug, a.slug)}
                className={`rounded-full border px-3 py-1.5 text-[13px] transition-colors ${area?.slug === a.slug ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary/50"}`}
              >
                {a.name} <span className={area?.slug === a.slug ? "opacity-80" : "text-muted-foreground"}>{count}</span>
              </Link>
            ))}
          </section>
        )}

        {/* Intro */}
        {copy.intro && (
          <section className="pt-8 pb-2 max-w-3xl">
            {copy.intro.split("\n\n").map((paragraph, i) => (
              <p key={i} className="text-muted-foreground leading-relaxed mb-4 last:mb-0">
                {paragraph}
              </p>
            ))}
          </section>
        )}

        {/* Homes */}
        <section id="listings" className="py-10 scroll-mt-24">
          {propertiesLoading ? (
            <div className="py-12 text-center text-sm text-muted-foreground">Loading homes…</div>
          ) : homes.length === 0 ? (
            <div className="text-center py-12 bg-secondary/30 rounded-card px-6">
              <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">Nothing here right now</h2>
              <p className="text-muted-foreground max-w-xl mx-auto">
                No home in {cityLabel} matches this collection at the moment. Browse everything, or save an alert and we will email you when one is listed.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Button asChild>
                  <Link to="/properties">Browse all homes</Link>
                </Button>
                {area && (
                  <Button asChild variant="outline">
                    <Link to={collectionPath(collection.slug)}>All of {tenant.cityLabel}</Link>
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-serif text-2xl font-semibold text-foreground">
                  Homes ({homes.length})
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {homes.map((p, i) => (
                  <PropertyCard key={p.id} property={p} isFavorite={isFavorite(p.id)} onToggleFavorite={toggleFavorite} priority={i < 3} />
                ))}
              </div>
            </>
          )}
        </section>

        {/* The standard */}
        {standard.length > 0 && (
          <section className="py-8 border-t border-border">
            <h2 className="font-serif text-2xl font-semibold text-foreground mb-2">What to expect</h2>
            <p className="text-muted-foreground text-sm mb-5 max-w-2xl">
              The features that make a home fit this collection. Every listing answers them, so you can compare like with like.
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {standard.map((a) => (
                <li key={a.key} className="flex items-start gap-3 rounded-card border border-border bg-card p-4">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <AmenityIcon name={a.icon} className="h-4 w-4 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-foreground leading-tight">{a.label}</p>
                    {a.hint && <p className="text-[13px] text-muted-foreground mt-0.5">{a.hint}</p>}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        <ThingsToDoList items={thingsToDo} heading={`Things to do near ${cityLabel}`} className="py-8 border-t border-border" />

        {/* FAQ */}
        {collection.faq.length > 0 && (
          <section className="py-8 border-t border-border max-w-3xl">
            <h2 className="font-serif text-2xl font-semibold text-foreground mb-5">Good to know</h2>
            <div className="divide-y divide-border rounded-card border border-border bg-card">
              {collection.faq.map((f, i) => (
                <details key={i} className="group px-5 py-4 [&_summary::-webkit-details-marker]:hidden">
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-3 font-medium text-foreground">
                    <span>{f.q}</span>
                    <span aria-hidden className="text-muted-foreground transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Siblings */}
        <CollectionLinks
          collections={collections}
          properties={properties}
          ctx={ctx}
          area={area ? { slug: area.slug, name: area.name } : null}
          excludeSlug={collection.slug}
          variant="cards"
          heading={`More ways to browse ${cityLabel}`}
          className="py-8 border-t border-border"
        />

        {/* Two-sided CTA: every guest page ends with the owner pitch. */}
        <section className="mt-8 rounded-card border border-border bg-secondary/40 p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <p className="font-serif text-xl font-semibold text-foreground">Own a home in {tenant.cityLabel}?</p>
            <p className="text-sm text-muted-foreground mt-1">List it free and reach renters looking for exactly this.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button asChild variant="outline" className="rounded-full">
              <Link to="/collections">
                <ArrowLeft className="h-4 w-4 mr-1" /> All collections
              </Link>
            </Button>
            <Button asChild className="rounded-full">
              <Link to="/owner-onboarding" onClick={() => setIntent("owner")}>
                <HomeIcon className="h-4 w-4 mr-1" /> List your home
              </Link>
            </Button>
          </div>
        </section>
      </div>

      <AuthPromptDialog open={authPromptOpen} onOpenChange={setAuthPromptOpen} />
    </div>
  );
};

export default CollectionPage;
